import { useEffect } from "react";
import { useDispatch } from "react-redux";

import { useAppSelector } from "@/store/store";
import { actions } from "@/store/slices/apartmentFiltersSlice";
import { useAppData } from "@/hooks/useAppData";
import { useAuth } from "@/hooks/useAuth";
import {
    useGetAllTagsQuery,
    useGetMunicipalitySectionsQuery,
    useGetGroupsQuery,
    useGetOwnerGroupsQuery,
} from "@/store/booking-management-api";
import { useGetApartmentsQuery } from "@/store/slices/apartments";

/**
 * Watches the active option lists exposed by `useAppData()` and prunes/resets
 * any apartment-filter selection that no longer maps to an available option.
 *
 * Behaviour:
 *  - `municipalitySectionId !== 0` and not present in `activeMunicipalitySectionOptions`
 *    → dispatch `setMunicipalitySection(0)` (the "Any" sentinel).
 *  - `ownerGroupId !== 0` and not present in `activeOwnerGroupOptions`
 *    → dispatch `setOwnerGroup(0)` (the "All" sentinel).
 *  - `groupId !== 0` and not present in `groupOptions`
 *    → dispatch `setGroup(0)` (the "All" sentinel). Without this a deleted
 *    apartment group leaves a persisted filter that matches nothing, so every
 *    page shows an empty portfolio with no visible cause.
 *  - Any id in `tagIds` that's no longer in the active `tags` query result
 *    → dispatch `setTagIds(prunedList)` keeping the still-valid ids.
 *
 * The reset path uses the existing setters, which already persist to
 * localStorage — so localStorage stays in sync without bespoke handling.
 *
 * Each check is gated on the relevant RTK Query reaching `isSuccess` so a
 * still-loading list (which transiently looks empty) never wipes a
 * legitimately-seeded filter on first render.
 *
 * Designed to be invoked once at the layout level (`AdminLayout`) so it runs
 * on every admin page.
 */
export function useResetUnavailableFilterValues(): void {
    const dispatch = useDispatch();
    const { user } = useAuth();
    const isAdmin = user?.isAdmin ?? false;

    const { municipalitySectionId, ownerGroupId, groupId, tagIds } = useAppSelector((state) => state.apartmentFilters);
    const { activeMunicipalitySectionOptions, activeOwnerGroupOptions, groupOptions } = useAppData();

    // Source data for each dimension. Only act once the corresponding source has
    // successfully loaded — otherwise an empty option list during initial fetch
    // would erroneously reset a persisted filter.
    const apartmentsQuery = useGetApartmentsQuery(isAdmin);
    const municipalitySectionsQuery = useGetMunicipalitySectionsQuery();
    const ownerGroupsQuery = useGetOwnerGroupsQuery();
    const tagsQuery = useGetAllTagsQuery();
    // `getGroups` always targets the admin endpoint, so it is skipped for
    // non-admins — who never see the Group control in the first place.
    const groupsQuery = useGetGroupsQuery(undefined, { skip: !isAdmin });

    const apartmentsLoaded = apartmentsQuery.isSuccess;
    const municipalitySectionsLoaded = municipalitySectionsQuery.isSuccess;
    const ownerGroupsLoaded = ownerGroupsQuery.isSuccess;
    const tagsLoaded = tagsQuery.isSuccess;
    const groupsLoaded = groupsQuery.isSuccess;

    useEffect(() => {
        if (!apartmentsLoaded || !municipalitySectionsLoaded) return;
        if (municipalitySectionId === 0) return;
        const target = municipalitySectionId.toString();
        const stillAvailable = activeMunicipalitySectionOptions.some((option) => option.value === target);
        if (!stillAvailable) {
            dispatch(actions.setMunicipalitySection(0));
        }
    }, [apartmentsLoaded, municipalitySectionsLoaded, municipalitySectionId, activeMunicipalitySectionOptions, dispatch]);

    useEffect(() => {
        if (!apartmentsLoaded || !ownerGroupsLoaded) return;
        if (ownerGroupId === 0) return;
        const target = ownerGroupId.toString();
        const stillAvailable = activeOwnerGroupOptions.some((option) => option.value === target);
        if (!stillAvailable) {
            dispatch(actions.setOwnerGroup(0));
        }
    }, [apartmentsLoaded, ownerGroupsLoaded, ownerGroupId, activeOwnerGroupOptions, dispatch]);

    useEffect(() => {
        if (!groupsLoaded) return;
        if (groupId === 0) return;
        const target = groupId.toString();
        const stillAvailable = groupOptions.some((option) => option.value === target);
        if (!stillAvailable) {
            dispatch(actions.setGroup(0));
        }
    }, [groupsLoaded, groupId, groupOptions, dispatch]);

    useEffect(() => {
        if (!tagsLoaded) return;
        if (tagIds.length === 0) return;
        const validTagIds = new Set((tagsQuery.data ?? []).map((tag) => tag.id));
        const pruned = tagIds.filter((id) => validTagIds.has(id));
        if (pruned.length !== tagIds.length) {
            dispatch(actions.setTagIds(pruned));
        }
    }, [tagsLoaded, tagIds, tagsQuery.data, dispatch]);
}

export default useResetUnavailableFilterValues;
